import { AxiosError } from 'axios';

import { apiClient, parseApiFailure } from '@/services/api/client';
import type { ApiEnvelope } from '@/types/api';
import type { AuthProfile, AuthSessionPayload } from '@/types/auth';

export interface LoginBody {
  email: string;
  password: string;
}

export interface RegisterBody {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  password: string;
}

export async function login(body: LoginBody): Promise<AuthSessionPayload> {
  try {
    const response = await apiClient.post<ApiEnvelope<AuthSessionPayload>>('/api/v1/auth/login', body);
    const envelope = response.data;
    if (!envelope.success) {
      throw new Error(envelope.message);
    }
    return envelope.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(parseApiFailure(error).message);
    }
    throw error;
  }
}

export async function register(body: RegisterBody): Promise<{ user: AuthProfile }> {
  try {
    const response = await apiClient.post<
      ApiEnvelope<{
        user: AuthProfile;
      }>
    >('/api/v1/auth/register', body);
    const envelope = response.data;
    if (!envelope.success) {
      throw new Error(envelope.message);
    }
    return envelope.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(parseApiFailure(error).message);
    }
    throw error;
  }
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/api/v1/auth/logout', {});
  } catch {
    /* Session revocation is best-effort once tokens are cleared locally */
  }
}

export async function fetchProfile(): Promise<AuthProfile> {
  try {
    const response = await apiClient.get<ApiEnvelope<{ profile: AuthProfile }>>('/api/v1/auth/profile');
    const envelope = response.data;
    if (!envelope.success) {
      throw new Error(envelope.message);
    }
    return envelope.data.profile;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(parseApiFailure(error).message);
    }
    throw error;
  }
}
